import { Star, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatVND } from '@/lib/format';
import { categoryDef } from '@/lib/constants';
import type { Monitor } from '@/lib/api';

export interface SkuMobileListProps {
  monitors: Monitor[];
  onSelect?: (m: Monitor) => void;
}

/**
 * Mobile-only compact card list for /table (Price List) — replace table when viewport < md.
 * Each row shows the data partner cares about most: SKU, name, key specs, Giá Web.
 * Tap → call onSelect (opens SKU detail modal).
 */
export function SkuMobileList({ monitors, onSelect }: SkuMobileListProps) {
  return (
    <div className="space-y-2">
      {monitors.map((m) => {
        const cat = categoryDef(m.category);
        const specs = [
          m.size ? `${m.size}"` : null,
          m.resolution,
          m.refresh_rate ? `${m.refresh_rate}Hz` : null,
        ].filter(Boolean) as string[];

        return (
          <div
            key={m.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect?.(m)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(m);
              }
            }}
            className="cursor-pointer rounded-lg border bg-card p-3 transition-colors hover:border-primary/30 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* Top: SKU id + brand badge */}
            <div className="flex items-center gap-2 text-[11px]">
              {Boolean(m.is_focal) && (
                <Star className="h-3 w-3 shrink-0 fill-cat-focal text-cat-focal" />
              )}
              {cat && <cat.icon className={cn('h-3 w-3 shrink-0', cat.colorClass)} />}
              <span className="truncate font-mono uppercase tracking-wider text-muted-foreground">
                {m.id}
              </span>
              <span className="ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5 font-semibold text-foreground">
                {m.brand}
              </span>
            </div>

            {/* Middle: product name */}
            <div className="mt-1.5 line-clamp-2 text-sm font-medium leading-tight" title={m.name}>
              {m.name}
            </div>

            {/* Bottom: specs + price Web */}
            <div className="mt-2 flex items-end justify-between gap-2 border-t pt-2">
              <div className="flex flex-wrap gap-1">
                {specs.length === 0 ? (
                  <span className="text-[11px] text-muted-foreground">—</span>
                ) : (
                  specs.map((s) => (
                    <span
                      key={s}
                      className="rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground"
                    >
                      {s}
                    </span>
                  ))
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="text-sm font-semibold tabular-nums text-primary">
                  {formatVND(m.price_web)}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
